import {
    FC,
    ReactNode,
    RefObject,
    createContext,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
} from 'react';
import { HiOutlineArrowDownTray, HiOutlineEye } from 'react-icons/hi2';
import Preloader from '@srm/ui/preloader';
import DataTable, { DataTableWrapMode, IDataTableColumn, IDataTableHandle } from '@srm/ui/data-table';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { useStableHandlers } from '@srm/ui/hooks/useStableHandlers';
import ActionButton from '@molecules/RowActionButton/ActionButton';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { AttachActionTypes } from '@models/actions/AttachActionTypes';
import { IUseAttachGridData } from '../../useAttachGridData';
import { IUseAttachSelection } from '../../useAttachSelection';
import { IAttachRow } from '../../AttachGrid.types';
import { buildAttachColumns, isVersionColumn } from '../../attachColumns';
import { rowValue } from '../../attachRowView';

interface IAttachTableProps {
    controlRef?: RefObject<IDataTableHandle<IAttachRow> | null>;
    data: IUseAttachGridData;
    selection: IUseAttachSelection;
    /** Есть выбранные строки — клик по имени файла не скачивает. */
    selectionActive: boolean;
    filterVisible: boolean;
    actionService?: AttachActionsService;
    onDownload?: (row: IAttachRow) => void;
    /** Файл строки скачивается: на кнопке «Скачать» — ход, повтор недоступен. */
    isDownloading?: (row: IAttachRow) => boolean;
    onPreview?: (row: IAttachRow) => void;
    wrapMode?: DataTableWrapMode;
    readOnly?: boolean;
    emptyState?: {
        title: string;
        hint: string;
        icon: ReactNode;
    };
    /** Туллбар грида над таблицей — прилипает вместе с её шапкой. */
    stickyLeadRef?: RefObject<HTMLElement | null>;
}

// «Скачать» вынесено отдельной кнопкой — в меню действий строки не дублируем.
const HIDDEN_ROW_ACTIONS = [AttachActionTypes[AttachActionTypes.Download]];

const attachRowId = (row: IAttachRow) => row.key;

// Таблица кэширует отрисованные строки: то, что меняется без смены данных строки (ход скачивания,
// подгруженные версии), доходит до ячеек контекстом — его потребители перерисуются и внутри
// закэшированной строки.
const AttachDownloadContext = createContext<((row: IAttachRow) => boolean) | undefined>(undefined);

interface IAttachVersionsState {
    childrenByParent: Record<string, IAttachRow[]>;
    loadingChildren: ReadonlySet<string>;
}

const AttachVersionsContext = createContext<IAttachVersionsState>({ childrenByParent: {}, loadingChildren: new Set() });

interface IAttachRowMenuProps {
    row: IAttachRow;
    service: AttachActionsService;
    onModifyData: () => void;
}

/** Меню действий строки — заметное дерево на каждую строку: не перерисовывается вместе с соседними кнопками. */
const AttachRowMenu = memo(({ row, service, onModifyData }: IAttachRowMenuProps) => (
    <ActionButton
        objId={row.key}
        objData={row.raw}
        service={service}
        onModifyData={onModifyData}
        excludeActionTypes={HIDDEN_ROW_ACTIONS}
    />
));
AttachRowMenu.displayName = 'AttachRowMenu';

interface IAttachRowActionsProps extends IAttachRowMenuProps {
    onPreview: (row: IAttachRow) => void;
    onDownload: (row: IAttachRow) => void;
}

const AttachRowActions = memo(({ row, service, onModifyData, onPreview, onDownload }: IAttachRowActionsProps) => {
    const downloading = useContext(AttachDownloadContext)?.(row) ?? false;
    return (
        <>
            <button type="button" className="atb__act" title="Предпросмотр" onClick={() => onPreview(row)}>
                <HiOutlineEye aria-hidden />
            </button>
            <button
                type="button"
                className="atb__act atb__act--download"
                title="Скачать"
                aria-busy={downloading || undefined}
                disabled={downloading}
                onClick={() => onDownload(row)}
            >
                {downloading ? (
                    <Preloader size={16} variant="inherit" position="inline" />
                ) : (
                    <HiOutlineArrowDownTray aria-hidden />
                )}
            </button>
            <span className="atb__act atb__act--menu">
                <AttachRowMenu row={row} service={service} onModifyData={onModifyData} />
            </span>
        </>
    );
});
AttachRowActions.displayName = 'AttachRowActions';

interface IAttachVersionsProps {
    rowKey: string;
    columns: IDataTableColumn<IAttachRow>[];
    rowActions: (row: IAttachRow) => ReactNode;
}

/** Версии файла — детейл-панель раскрытой строки (ленивая подгрузка). */
const AttachVersions = memo(({ rowKey, columns, rowActions }: IAttachVersionsProps) => {
    const { childrenByParent, loadingChildren } = useContext(AttachVersionsContext);
    const children = childrenByParent[rowKey];
    if (loadingChildren.has(rowKey)) return <div className="atb__vrow-loading">Загрузка версий…</div>;
    if (!children?.length) return <div className="atb__vrow-loading">Нет версий</div>;
    return (
        <div className="atb__versions atb__versions--next">
            <DataTable
                columns={columns}
                data={children}
                getRowId={attachRowId}
                paging={false}
                rowActions={rowActions}
                rowActionsPosition="right"
                rowActionsAlwaysVisible
                rowActionsWidth={104}
            />
        </div>
    );
});
AttachVersions.displayName = 'AttachVersions';

/**
 * Табличный вид вложений на базовом `@atoms/DataTable` (этап N9): сортировка,
 * фильтр-строка, пагинация и виртуализация — движок атома, а рендеры ячеек,
 * действия строки и раскрытие версий — собственные рендеры грида вложений.
 */
const AttachTable: FC<IAttachTableProps> = ({
    controlRef,
    data,
    selection,
    selectionActive,
    filterVisible,
    actionService,
    onDownload,
    isDownloading,
    onPreview,
    wrapMode,
    readOnly = false,
    emptyState,
    stickyLeadRef,
}) => {
    const selectionActiveRef = useLatest(selectionActive);
    const columns = useMemo(
        () =>
            buildAttachColumns(data.columns, {
                readOnly,
                isSelectionActive: () => selectionActiveRef.current,
                onDownload,
            }),
        [data.columns, readOnly, selectionActiveRef, onDownload],
    );

    // версию ищем среди ВСЕХ колонок: значение лежит в строке, даже если столбец скрыт
    const versionColumn = useMemo(() => data.columns.find(isVersionColumn), [data.columns]);
    const canExpandRow = useCallback(
        (row: IAttachRow) => {
            if (!row.expandable) return false;
            if (!versionColumn) return true;
            const total = parseInt(String(rowValue(row, versionColumn.field) ?? '').split('/')[1] ?? '', 10);
            return Number.isNaN(total) ? row.expandable : total > 1;
        },
        [versionColumn],
    );

    // Меню строки (rowActions) держит обработчик своего рендера — перечитывание берёт актуальную функцию грида.
    const { onModifyData } = useStableHandlers({ onModifyData: () => data.refresh() });

    const rowActions = useCallback(
        (row: IAttachRow) => {
            if (readOnly || !onPreview || !onDownload || !actionService) return null;
            return (
                <AttachRowActions
                    row={row}
                    service={actionService}
                    onModifyData={onModifyData}
                    onPreview={onPreview}
                    onDownload={onDownload}
                />
            );
        },
        [readOnly, onPreview, onDownload, actionService, onModifyData],
    );

    const renderDetail = useCallback(
        (row: IAttachRow) => <AttachVersions rowKey={row.key} columns={columns} rowActions={rowActions} />,
        [columns, rowActions],
    );
    const versionsState = useMemo<IAttachVersionsState>(
        () => ({ childrenByParent: data.childrenByParent, loadingChildren: data.loadingChildren }),
        [data.childrenByParent, data.loadingChildren],
    );

    // Мост к общему выбору вложений (bulk-бар, действия над выбранными). Таблица видит только
    // текущую страницу и при её смене сама снимает отметки ушедших строк — поэтому в общий
    // выбор переносим разницу, а снятие учитываем лишь у показанных строк.
    const tableKeysRef = useRef<Set<string>>(new Set());
    const pageKeysRef = useRef<Set<string>>(new Set());
    pageKeysRef.current = useMemo(() => new Set(data.rows.map((row) => row.key)), [data.rows]);
    const { apply } = selection;
    const onSelectionChange = useCallback(
        (keys: string[]) => {
            const prev = tableKeysRef.current;
            const next = new Set(keys);
            tableKeysRef.current = next;
            apply(
                keys.filter((k) => !prev.has(k)),
                [...prev].filter((k) => !next.has(k) && pageKeysRef.current.has(k)),
            );
        },
        [apply],
    );

    // Отметки страницы — из общего выбора: при возврате на страницу и после «Выбрать все N».
    useEffect(() => {
        const table = controlRef?.current;
        if (!table) return;
        const wanted = data.rows.filter((row) => selection.selected.has(row.key)).map((row) => row.key);
        const current = tableKeysRef.current;
        if (wanted.length === current.size && wanted.every((k) => current.has(k))) return;
        table.selectRows(wanted);
    }, [controlRef, data.rows, selection.selected]);

    return (
        <div className="atb__tablewrap atb__tablewrap--next">
            <AttachDownloadContext.Provider value={isDownloading}>
                <AttachVersionsContext.Provider value={versionsState}>
                    <DataTable
                        controlRef={controlRef}
                        columns={columns}
                        data={data.rows}
                        getRowId={attachRowId}
                        // вид строки зависит от её данных, выбора и раскрытия; ход скачивания и версии
                        // приходят контекстом — набор символа в поиске не перерисовывает строки
                        cacheRows
                        // страницы вложений ведёт сам грид (серверная выборка + AttachPager),
                        // поэтому внутренний пейджер таблицы выключен — иначе их два
                        paging={false}
                        stickyHeader
                        stickyLeadRef={stickyLeadRef}
                        showFilterRow={filterVisible}
                        selectable={!readOnly && data.options?.selectionMode !== 'none'}
                        showSelectAll={data.options?.allowSelectAll}
                        onSelectionChange={onSelectionChange}
                        rowActions={rowActions}
                        rowActionsPosition="right"
                        rowActionsAlwaysVisible
                        rowActionsWidth={104}
                        rowActionsHeader={<span className="atb__hactions">Действия</span>}
                        canExpandRow={canExpandRow}
                        renderDetail={renderDetail}
                        onExpandRow={(row) => data.toggleExpand(row.key)}
                        loadingRowIds={data.loadingChildren}
                        // контролируемый режим: им владеет грид вложений (шестерёнка тулбара,
                        // сохранённые виды) — defaultWrapMode применялся бы только на маунте
                        wrapMode={wrapMode}
                        // Пустое состояние DataTable находится внутри общей зоны таблицы:
                        // шапка и сообщение образуют один непрерывный контейнер.
                        showEmptyState={!!emptyState}
                        emptyText={emptyState?.title}
                        emptyHint={emptyState?.hint}
                        emptyIcon={emptyState?.icon}
                    />
                </AttachVersionsContext.Provider>
            </AttachDownloadContext.Provider>
        </div>
    );
};

export default AttachTable;
